import React, { useState, useEffect, useMemo } from 'react';
import { FlatList, ScrollView, TouchableOpacity, View } from 'react-native'

import api from "../../../../../utils/api"
import tw from '../../../../../styles/tailwind';
import { Style } from 'twrnc/dist/esm/types';

import Text from '../../../../../components/Text';
import Icon from '../../../../../components/IcomoonIcon';
import { Image } from 'expo-image';
import { useQuery } from '@tanstack/react-query';
import IconButton from '../../../../../components/IconButton';
import AddPaymentModal from './AddPaymentModal';
import Header from '../../../../../navigation/Header';
import EditFeeModal from './EditFeeModal';
import SearchInput from '../../../../../components/SearchInput';
import Animated, { Extrapolation, interpolate, useAnimatedStyle, useSharedValue, withSpring, withTiming } from 'react-native-reanimated';
import TabsSelector from '../../../../../components/TabsSelector';
import useActiveAdminCommunity from '@/hooks/useActiveAdminCommunity';
import useOpenDetailParam from '@/hooks/useOpenDetailParam';



// https://github.com/software-mansion/react-native-reanimated/issues/3355#issuecomment-1518942329
if (typeof window !== 'undefined') {
  window._frameTimestamp = null;
}

export function useProgressPercentAnimation(progressPercent=0){
  const percentageWidth = useSharedValue(0)

  const percentageBarStyle = useAnimatedStyle(() => {
    const interpolatedWidth = interpolate(
      percentageWidth.value,
      [0, 1],
      [0, 100],
      { extrapolateRight: Extrapolation.CLAMP }
    );

    return {
      width: withSpring(`${interpolatedWidth}%`, {
        stiffness: 75,
        mass: 0.6,
      }),
    };

  });

  useEffect(() => {
    percentageWidth.value = withSpring(progressPercent / 100)
  }, [progressPercent]);


  // console.log(progressPercent, percentageBarStyle.width);

  return percentageBarStyle
}


export function ProgressBar(
  { color = 'white', progressPercent = 0, containerStyle = ``, barStyle=``}:
    { color?: string, progressPercent: number, containerStyle: string | Style, barStyle: string | Style }
){
  const percentageBarStyle = useProgressPercentAnimation(progressPercent)  

  return (
    <View style={
      tw.style(`border border-${color} h-2.8 rounded overflow-hidden`, containerStyle)
    }>
      {/* <View style={tw.style(`bg-${color} h-2.8 rounded w-${progressPercent}%`)}></View> */}
      <Animated.View
        style={[tw.style(`bg-${color} h-2.8 rounded`, barStyle), { ...percentageBarStyle }]}
      ></Animated.View>
    </View>
  )
}

function FeeAmountsSummary({ fee }) {
  return (
    <View style={tw``}>
      <View style={tw`row mb-1 items-end border-t border-b border-white/15 py-2`}>
        <View style={tw`row items-end`}>
          <Text style={tw`text-white font-600 text-2xl`}><Text style={tw`text-red font-600`}>$</Text>{fee?.pending_amount?.toLocaleString() ?? "-"}</Text>
          <View style={tw`bg-red rounded-full px-2.5 py-0.5 ml-2 mb-1`}>
            <Text style={tw`text-white text-xs font-600`}>Por cobrar</Text>
          </View>
        </View>

        <Text style={tw`text-white ml-3 text-sm mb-1.2`}>({fee?.pending_units ?? "-"} restantes)</Text>
      </View>


      <View style={tw`row justify-between mt-1 mb-1`}>
        <Text style={tw.style(`text-white`)}><Text style={tw`font-600 text-xl`}>${fee?.charged_amount?.toLocaleString()}</Text> cobrado</Text>
        <Text style={tw.style(`text-white ml-2 text-white font-600 text-xl`)}>{fee?.charged_units_percent}%</Text>
      </View>

      <ProgressBar color={`white`} progressPercent={fee?.charged_units_percent} />
    </View>
  )
}


function FeeRow({item, index, onPress, selected}){
  return (
    <TouchableOpacity
      style={tw.style(
        `row justify-between px-5 h-15`,
        index % 2 == 1 && `bg-gray-100`,
        selected && `bg-selected border border-selected-border rounded-xl`
      )}
      onPress={onPress}
    >
      <View style={tw``}>
        <Text style={tw``}>{item.unit.name}</Text>

        {
          item.unit.owners?.length > 0 &&
          <Text style={tw`text-gray-400`}>{item.unit.owners[0].name}</Text>
        }
      </View>

      <View style={tw`items-end`}>
        {
          item.paid > 0 &&
          <View style={tw`row`}>
            <Text style={tw`mr-4 text-primary`}>${item.paid}</Text>
            <Icon name='check' color={tw.color('primary')} />
          </View>
        }
        {
          item.pending > 0 &&
          <View style={tw`row`}>
            <Text style={tw`mr-4 text-red-600`}>${item.pending}</Text>
            <Icon name='cancel' color={tw.color('red-600')} />
          </View>
        }

        {/* // <Text style={tw`text-gray-400`}>{item.fee_type}</Text> */}
      </View>
    </TouchableOpacity>
  )
}



export default function AdminFeeDetailScreen({ navigation, route }) {
  const { feeId } = route.params
  const {communityId} = useActiveAdminCommunity()
  const openFeeUnitId = useOpenDetailParam(navigation, 'Admin.FeeUnit.Detail', 'unit_fee_id', true)

  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showEditFeeModal, setShowEditFeeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [shownPaymentsFilter, setShownPaymentsFilter] = useState("paid");

  const { isLoading, error, data: feeDetail, refetch } = useQuery({
    queryKey: ['community.fees.detail', communityId, feeId],
    queryFn: () => api.get(`/communities/${communityId}/fees/${feeId}`).then(response => response.data),
  })


  function goToFeeUnitScreen(feeUnit){
    navigation.navigate("Admin.FeeUnit.Detail", { 
      communityId,
      unit_fee_id: feeUnit.uuid,
      onFeeEdited: () => {
        refetch()
      }
    })
  }


  const filteredUnits = useMemo(() => {
    if (!feeDetail?.units) return []

    // const sortedFeeUnits = feeDetail.units.sort((a, b) => {
    //   if (a.unit.street_name > b.unit.street_name) {
    //     return 1
    //   }
    //   if (b.unit.street_name > a.unit.street_name) {
    //     return -1
    //   }
    //   return 0
    // })

    const sortedFeeUnits = feeDetail.units

    // if (searchQuery == "") return sortedFeeUnits
    
    const filtered = sortedFeeUnits.filter(({unit, is_paid}) => {
      if(shownPaymentsFilter === "paid" && !is_paid) return false
      if(shownPaymentsFilter === "pending" && is_paid) return false

      if (searchQuery == "") return true

      const regex = new RegExp(searchQuery, "i")
      const nameMatches = unit.name.match(regex)
      const ownersMatches = unit.owners.some(ow => ow.user.name.match(regex))

      return nameMatches || ownersMatches
    })

    return filtered
  }, [searchQuery, feeDetail?.units, shownPaymentsFilter])

  // const shownPayments = useMemo(() => {
  //   if (reportOverview?.payments) {
  //     return reportOverview?.payments.filter(e => shownPaymentsFilter == "all" ? true : (
  //       e.is_paid == (shownPaymentsFilter == "paid")
  //     ))
  //   }
  //   return []
  // }, [reportOverview?.payments, shownPaymentsFilter])



  return (
    <View style={tw`flex-1 bg-white`}>
      <Header
        showBackButton
        style={tw.style(`bg-secondary pb-4 rounded-b-0 mb-0`, feeDetail?.fee.type.type == "other" && `bg-primary`)}
        backButtonColor='white'
        // Alineada con el nombre de la cuota, no con el centro de todo el header.
        backButtonStyle={{ bottom: 'auto', height: 32 }}
      >
        <View style={tw`flex-1 -ml-9`}>
          <View style={tw`row justify-between mb-1 pl-9 items-start`}>
            <View style={tw`flex-1 ml-1 -mt-2`}>
              <Text style={tw`text-lg font-600 text-white`}>{feeDetail?.fee.name ?? '-'}</Text>
              <Text style={tw`text-white -mt-1`}>{feeDetail?.fee.type.name}</Text>
            </View>

            <TouchableOpacity style={tw`py-1 px-2 -mr-2`} onPress={() => setShowEditFeeModal(true)}>
              <Icon name='settings' color={tw.color('white')} size={20} />
            </TouchableOpacity>
          </View>

          {/*  */}
          <FeeAmountsSummary fee={feeDetail?.fee} />

          <SearchInput
            style={{ input: `bg-gray-100`, container: `mt-4` }}
            placeholder='Buscar vivienda'
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </View>
      </Header>
  
      <View>
        <TabsSelector
          options={[
            { label: "Pagados", value: "paid" },
            { label: "Pendientes", value: "pending" },
          ]}
          onChange={option => setShownPaymentsFilter(option)}
        />
      </View>
      


      {/* <Text>{JSON.stringify(fee)}</Text> */}


      {/* header */}
      <View style={tw`bg-gray-100/1 px-5 py-2 row justify-between`}>
        <Text style={tw`text-gray-400 font-bold`}>Vivienda</Text>

        <View style={tw`row`}>
          <Text style={tw`text-gray-400 font-bold`}>Monto / Tipo</Text>
        </View>
      </View>



      <FlatList
        data={filteredUnits}
        extraData={openFeeUnitId}
        contentContainerStyle={tw`pb-18`}
        renderItem={({ item, index }) =>
          <FeeRow item={item} index={index} selected={item.uuid === openFeeUnitId} onPress={() => goToFeeUnitScreen(item)} />
        }
        keyExtractor={item => item.id}
      />

      <IconButton
        title={"Registrar Pago"}
        color='secondary'
        textColor='white'
        style={tw`absolute bottom-4 self-center`}
        onPress={() => setShowAddPaymentModal(true)}
      />

      <AddPaymentModal
        onRequestClose={() => setShowAddPaymentModal(false)}
        visible={showAddPaymentModal}
        communityId={communityId}
        onPaymentCreated={() => {
          setShowAddPaymentModal(false)
          refetch()
        }}
      />

      <EditFeeModal
        fee={feeDetail}
        communityId={communityId}
        visible={showEditFeeModal}
        onRequestClose={() => setShowEditFeeModal(false)}
        onFeeEdited={() => {
          setShowEditFeeModal(false)
          refetch()
        }}
      />
    </View>
  );
}
